import { useState } from "react";
import { imgProxy } from "@/lib/img";
import { ExternalLink, Copy, MapPin, Layers, Crown, Medal } from "lucide-react";

export function FeaturedBadge({ tier, size = "sm", compact = false, testid }) {
  if (tier !== "gold" && tier !== "silver") return null;
  const gold = tier === "gold";
  const Icon = gold ? Crown : Medal;
  const label = gold ? "Ouro" : "Prata";
  const ring = gold ? "0 0 0 1px rgba(200,170,110,.6)" : "0 0 0 1px rgba(203,213,225,.6)";
  if (compact) {
    return (
      <span
        data-testid={testid || `featured-badge-${tier}`}
        title={`Destaque ${label}`}
        aria-label={`Destaque ${label}`}
        className={`inline-flex items-center justify-center w-5 h-5 ${gold ? "text-black bg-[#c8aa6e]" : "text-black bg-[#cbd5e1]"}`}
        style={{ boxShadow: ring }}
      >
        <Icon className="w-3 h-3" strokeWidth={2.25} />
      </span>
    );
  }
  const pad = size === "lg" ? "text-[11px] px-2.5 py-1" : "text-[10px] px-2 py-0.5";
  return (
    <span
      data-testid={testid || `featured-badge-${tier}`}
      className={`inline-flex items-center gap-1 font-semibold ${pad} ${gold ? "text-black bg-[#c8aa6e]" : "text-black bg-[#cbd5e1]"}`}
      style={{ boxShadow: ring }}
    >
      <Icon className="w-3 h-3" strokeWidth={2} /> {label}
    </span>
  );
}
import FavoriteButton from "@/components/dashboard/FavoriteButton";
import WhatsAppButton from "@/components/dashboard/WhatsAppButton";

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1603239699774-885100e49753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/flagged/photo-1574243961314-cb2e12cc4c1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1676195470090-7c90bf539b3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85",
];

function fmtPhone(p) {
  if (!p) return null;
  return p.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
}

export default function ProfileCard({ p, index = 0, sourceColor = "#c8aa6e", onOpen = () => {}, fullDescription = false, testid }) {
  const [expanded, setExpanded] = useState(false);
  const photo = imgProxy(p.photo_url, 480) || PLACEHOLDERS[index % 4];
  const desc = (p.description || "").trim();
  const showToggle = fullDescription && desc.length > 160;
  const color = p.source_color || sourceColor;

  return (
    <div
      data-testid={testid || `profile-card-${index}`}
      className={`group bg-[#18181b] border border-[#27272a] hover:border-[#c8aa6e]/70 transition-colors flex flex-col overflow-hidden ${p.status === "inativo" ? "opacity-70" : ""}`}
      style={{ borderLeft: `3px solid ${color}` }}
    >
      {/* site color bar */}
      <div className="h-1 w-full" style={{ background: color }} data-testid={`card-colorbar-${index}`} />
      {/* Photo */}
      <button
        type="button"
        onClick={() => onOpen(p.id)}
        title="Ver detalhe do perfil"
        className="relative block w-full aspect-[4/5] overflow-hidden bg-[#09090b]"
        data-testid={`card-photo-${index}`}
      >
        <img
          src={photo}
          onError={(e) => (e.currentTarget.src = PLACEHOLDERS[index % 4])}
          alt={p.name || "Perfil"}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-300 bg-[#18181b]"
        />
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#09090b] to-transparent pointer-events-none" />
        {/* source badge */}
        <span
          className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-black px-2 py-0.5"
          style={{ background: color }}
        >
          {p.source_name}
        </span>
        {p.phone_ad_count > 1 && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] text-[#c8aa6e] bg-black/55 backdrop-blur px-1.5 py-0.5" title={`${p.phone_ad_count} anúncios com o mesmo número`}>
            <Copy className="w-3 h-3" /> {p.phone_ad_count}
          </span>
        )}
        {p.status === "inativo" && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-medium text-white bg-[#ef4444]/90 px-2 py-0.5" data-testid={`card-inactive-${index}`}>
            Desativado
          </span>
        )}
        {p.featured_tier && (
          <span className={`absolute ${p.status === "inativo" ? "top-8" : "top-2"} right-2`} data-testid={`card-featured-${index}`}>
            <FeaturedBadge tier={p.featured_tier} />
          </span>
        )}
      </button>

      {/* Body */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <button type="button" onClick={() => onOpen(p.id)} className="text-left min-w-0">
            <div className="text-sm text-[#fafafa] font-medium truncate group-hover:text-[#c8aa6e] transition-colors">{p.name || "—"}</div>
            <div className="flex items-center gap-1 text-[11px] text-[#71717a] mt-0.5">
              <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{p.city || "—"}</span>
            </div>
          </button>
          <FavoriteButton profileId={p.id} initial={p.is_favorite} size={16} />
        </div>

        {p.phone ? (
          <div className="flex items-center gap-2 text-[13px] font-mono text-[#e4e4e7]">
            {fmtPhone(p.phone)}
            <WhatsAppButton phone={p.phone} name={p.name} size={15} />
          </div>
        ) : (
          <div className="text-[11px] text-[#52525b]">Sem número</div>
        )}

        {desc && (
          <p className={`text-[12px] leading-relaxed text-[#a1a1aa] ${fullDescription && expanded ? "" : "line-clamp-3"}`}>
            {desc}
          </p>
        )}
        {showToggle && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="self-start text-[11px] text-[#c8aa6e] hover:text-[#d4b982]"
            data-testid={`card-desc-toggle-${index}`}
          >
            {expanded ? "ver menos" : "ver mais"}
          </button>
        )}

        <div className="mt-auto pt-2 flex items-center justify-between border-t border-[#27272a]">
          <button
            type="button"
            onClick={() => onOpen(p.id)}
            className="inline-flex items-center gap-1.5 text-[11px] text-[#c8aa6e] hover:text-[#d4b982]"
            data-testid={`card-open-${index}`}
          >
            <Layers className="w-3.5 h-3.5" /> Ver detalhe
          </button>
          {p.profile_url && (
            <a href={p.profile_url} target="_blank" rel="noreferrer" title="Abrir anúncio original"
              className="text-[#71717a] hover:text-[#c8aa6e]">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
